<template>
  <div class="uploader-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Import rozliczeń</h1>
      </div>
    </div>
    <v-card outlined class="rounded-lg content-card" style="overflow: hidden;">
      <div class="table-section-title">
        <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-cloud-upload</v-icon></div>
        <div class="filter-title">{{ $t('uploader.importSettlements') }}</div>
        <v-spacer></v-spacer>
      </div>

      <!-- Info: Bolt CSV jako źródło prawdy. Wgraj kiedykolwiek — auto-cofnie wstępne. -->
      <v-alert
        v-if="boltAutoImportActive && isBoltSelected"
        type="info"
        outlined dense
        class="mx-4 mb-2"
      >
        <strong>Bolt: cron Mon 8:00 tworzy import wstępny z API.</strong>
        Po wgraniu CSV z Bolt portalu („Pobierz" → „Zarobki na kierowcę") system automatycznie
        cofnie te wstępne importy i utworzy finalne rozliczenia z Twoich CSV-owych wartości
        (zgodne co do grosza z Bolt portalem).
      </v-alert>
      <v-form>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="appType"
                :items="appTypes"
                item-text="name"
                item-value="id"
                :label="$t('uploader.application')"
                required
                @change="getMathEquations()"
              />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="mathEq"
                :items="mathEqs"
                item-text="name"
                item-value="id"
                :label="$t('uploader.template')"
                required
                multiple
              />
            </v-col>
            <v-col cols="12">
              <v-file-input
                v-model="file"
                :label="$t('uploader.settlementFile')"
                accept=".csv, .xlsx"
              ></v-file-input>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="fileName"
                :label="$t('uploader.settlementName')"
                type="text"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="320"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="dateLabel"
                    label="Tydzień (kliknij dowolny dzień)"
                    prepend-icon="mdi-calendar-week"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :hint="dateWeekHint"
                    persistent-hint
                  />
                </template>
                <v-date-picker
                  :value="date"
                  show-week
                  first-day-of-week="1"
                  locale-first-day-of-year="4"
                  locale="pl"
                  no-title
                  @input="onDatePicked"
                />
              </v-menu>
            </v-col>
            <v-col cols="12">
              <v-btn
                class="primary"
                style="width: 100%"
                @click="importFiles()"
              >{{ $t('uploader.import') }}</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card>
    <v-card outlined class="mt-4 rounded-lg" style="overflow: hidden;">
      <v-card-title>
        <v-icon left color="primary">mdi-api</v-icon>
        Pobierz z API Bolta
        <v-spacer />
        <v-switch
          v-model="autoImportEnabled"
          :label="autoImportEnabled ? 'Auto: ON (pn 8:00)' : 'Auto: OFF'"
          :color="autoImportEnabled ? 'success' : 'grey'"
          dense hide-details inset
          @change="toggleAutoImport"
        />
      </v-card-title>
      <v-card-text class="caption">
        Backend woła API Bolta, buduje taki sam CSV jak z portalu i odpala standardowy import.
        Obsługuje wszystkie miasta skonfigurowane w integracji.
        Cron: <strong>poniedziałek 8:00</strong> — pobiera poprzedni tydzień (pn–ndz).
        Przełącznik po prawej wyłącza cron dla tej firmy (ręczny przycisk dalej działa).
      </v-card-text>

      <!-- Miasta Bolta ↔ Oddziały -->
      <v-container>
        <div class="subtitle-2 mb-2 d-flex align-center">
          <v-icon small left>mdi-map-marker-multiple</v-icon>
          Miasta Bolta / oddziały
          <v-btn x-small text @click="loadCities()" :loading="citiesLoading" class="ml-2">
            <v-icon x-small>mdi-refresh</v-icon>
          </v-btn>
          <v-spacer />
          <v-btn x-small outlined color="primary" @click="autoCreateAllCities()" :loading="autoCreateLoading"
                 :disabled="!cities.length">
            <v-icon x-small left>mdi-auto-fix</v-icon>
            Utwórz wszystkie brakujące
          </v-btn>
        </div>
        <v-simple-table dense v-if="cities.length">
          <thead>
            <tr>
              <th>Miasto Bolta</th>
              <th>Oddział</th>
              <th class="text-right">Akcja</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in cities" :key="c.boltCompanyId">
              <td>
                <strong v-if="c.boltCompanyName">{{ c.boltCompanyName }}</strong>
                <span v-else class="grey--text">(brak kursów)</span>
                <span class="caption grey--text ml-1">#{{ c.boltCompanyId }}</span>
                <v-chip v-if="!c.boltCompanyName" x-small color="grey lighten-2" class="ml-1">
                  martwe
                </v-chip>
              </td>
              <td>
                <span v-if="c.matched" class="success--text">
                  <v-icon small color="success">mdi-check</v-icon>
                  {{ c.subCompanyName }}
                </span>
                <span v-else class="warning--text">
                  <v-icon small color="warning">mdi-alert</v-icon>
                  Brak
                </span>
              </td>
              <td class="text-right">
                <v-btn v-if="!c.matched && c.boltCompanyName" x-small outlined color="primary"
                       @click="createCityAsSubCompany(c)" :loading="cityActionLoading === c.boltCompanyId">
                  <v-icon x-small left>mdi-plus</v-icon>Utwórz oddział
                </v-btn>
                <span v-else-if="!c.matched" class="caption grey--text">
                  (nie tworzymy)
                </span>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="caption grey--text">
          Kliknij odśwież żeby pobrać listę miast z API Bolta.
        </div>
      </v-container>
      <v-container>
        <v-row dense>
          <v-col cols="12">
            <v-menu
              v-model="apiWeekMenu"
              :close-on-content-click="false"
              offset-y
              max-width="320"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="apiWeekLabel"
                  label="Tydzień (kliknij dowolny dzień)"
                  prepend-icon="mdi-calendar-week"
                  readonly
                  dense
                  v-bind="attrs"
                  v-on="on"
                  :hint="apiWeekHint"
                  persistent-hint
                />
              </template>
              <v-date-picker
                :value="apiFrom"
                show-week
                first-day-of-week="1"
                locale-first-day-of-year="4"
                locale="pl"
                no-title
                @input="onApiDatePicked"
              />
            </v-menu>
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="apiEquation"
              :items="boltMathEqs"
              item-text="name"
              item-value="id"
              label="Wzór (tylko BOLT)"
              dense
            />
          </v-col>
          <v-col cols="6">
            <v-btn outlined color="info" block @click="previewApi()" :loading="apiLoading">
              <v-icon left small>mdi-eye</v-icon>Podgląd CSV
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn color="primary" block @click="runApi()" :loading="apiLoading">
              <v-icon left small>mdi-download</v-icon>Pobierz i zaimportuj
            </v-btn>
          </v-col>
        </v-row>
        <v-expand-transition>
          <div v-if="apiPreview" class="mt-3">
            <div class="caption">
              Miasta: <strong>{{ apiPreview.cities.join(', ') }}</strong> •
              Zamówienia: <strong>{{ apiPreview.ordersCount }}</strong> •
              Kierowcy w CSV: <strong>{{ apiPreview.rowCount }}</strong>
            </div>
            <v-textarea
              :value="apiPreview.csv"
              label="CSV (preview)"
              rows="10"
              outlined dense readonly
              class="mt-2"
              style="font-family: monospace; font-size: 11px;"
            />
          </div>
        </v-expand-transition>
      </v-container>
    </v-card>

    <v-alert
      type="info"
      outlined dense
      class="mt-4"
    >
      <strong>Czynsz najmu auta — naliczany automatycznie raz w tygodniu.</strong>
      Po imporcie rozliczeniowym (Bolt/Uber/FreeNow) system tworzy proporcjonalne
      obciążenia czynszem na podstawie protokołów najmu (RENT_START / RENT_END)
      oraz stawki tygodniowej z floty.
      <br><br>
      <v-icon x-small left color="info">mdi-information</v-icon>
      <strong>Bezpieczne dla wielu CSV w jednym tygodniu</strong> — jeśli wgrasz
      6 plików (np. po jednym z każdego miasta), czynsz jest naliczany tylko
      raz per (kierowca, auto, tydzień). Kolejne importy tego samego tygodnia
      pomijają drivera, który już ma obciążenie.
      <br><br>
      <v-icon x-small left color="info">mdi-information</v-icon>
      <strong>Pro-rata per dzień:</strong> kierowca biorący auto np. od środy
      zapłaci 5/7 stawki, a nie cały tydzień.
    </v-alert>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);
export default {
  name: "UploaderPage",
  data: () => ({
    file: null,
    date: null,
    dateMenu: false,
    fileName: null,
    mathEqs: [],
    // Osobna lista dla sekcji "Pobierz i zaimportuj" (API Bolta). Tamta sekcja
    // jest widoczna niezależnie od wybranej aplikacji, więc nie może korzystać
    // z `mathEqs` filtrowanych po aplikacji — inaczej dało się pobrać dane
    // z Bolta i policzyć je wzorem Ubera.
    boltMathEqs: [],
    mathEq: null,
    appType: null,
    apiFrom: null,
    apiTo: null,
    apiWeekMenu: false,
    apiEquation: null,
    apiPreview: null,
    apiLoading: false,
    autoImportEnabled: true,
    cities: [],
    citiesLoading: false,
    cityActionLoading: null,
    autoCreateLoading: false,
appTypes:[{
  id: 'BOLT',
  name: 'BOLT'
},{
  id: 'UBER',
  name: 'UBER'
},{
  id: 'FREENOW',
  name: 'FREENOW'
},],
    rules: {
      required: (value) => !!value || "Wymagane.",
    },
  }),
  computed: {
    isBoltSelected() {
      return this.appType === 'BOLT';
    },
    boltAutoImportActive() {
      return this.autoImportEnabled === true;
    },
    dateLabel() {
      if (!this.date) return '';
      const d = dayjs(this.date);
      const start = d.startOf('isoWeek').format('DD.MM');
      const end = d.endOf('isoWeek').format('DD.MM.YYYY');
      return `Tydzień ${d.isoWeek()} • ${start} – ${end}`;
    },
    dateWeekHint() {
      if (!this.date) return 'Kliknij aby wybrać tydzień';
      return `Import zostanie zapisany z datą ${dayjs(this.date).format('DD.MM.YYYY')} (poniedziałek)`;
    },
    apiWeekLabel() {
      if (!this.apiFrom || !this.apiTo) return '';
      const from = dayjs(this.apiFrom);
      const to = dayjs(this.apiTo);
      return `Tydzień ${from.isoWeek()} • ${from.format('DD.MM')} – ${to.format('DD.MM.YYYY')}`;
    },
    apiWeekHint() {
      if (!this.apiFrom || !this.apiTo) return 'Kliknij aby wybrać tydzień (pn–ndz)';
      return `Pobierze dane od ${dayjs(this.apiFrom).format('DD.MM')} do ${dayjs(this.apiTo).format('DD.MM.YYYY')}`;
    },
  },
  async created() {
    // Jedno pobranie wypełnia obie listy: `boltMathEqs` dla sekcji API Bolta
    // i `mathEqs` dla uploadu (pusta, dopóki nie wybrano aplikacji — wcześniej
    // pokazywały się tu wzory BOLT mimo niewybranej aplikacji, co zachęcało
    // do wgrania pliku Ubera pod wzorami Bolta).
    await this.getMathEquations();
    // Default range = PREVIOUS ISO week (pn-ndz) — to co cron importuje
    const now = new Date();
    const day = now.getDay() || 7;
    const monday = new Date(now); monday.setDate(now.getDate() - day + 1 - 7);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    this.apiFrom = monday.toISOString().slice(0, 10);
    this.apiTo = sunday.toISOString().slice(0, 10);
    // Load current auto-import state
    try {
      const res = await axios({
        method: 'get',
        url: process.env.VUE_APP_ROOT_API + 'bolt/status',
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      });
      this.autoImportEnabled = res.data?.data?.autoImportEnabled !== false;
    } catch (e) { /* not configured yet */ }
  },
  methods: {
    // Snap manual import date → poniedziałek wybranego tygodnia.
    onDatePicked(picked) {
      this.date = dayjs(picked).startOf('isoWeek').format('YYYY-MM-DD');
      this.dateMenu = false;
    },
    // Snap API panel → cały tydzień (pn–ndz) zawierający kliknięty dzień.
    onApiDatePicked(picked) {
      const d = dayjs(picked);
      this.apiFrom = d.startOf('isoWeek').format('YYYY-MM-DD');
      this.apiTo = d.endOf('isoWeek').format('YYYY-MM-DD');
      this.apiWeekMenu = false;
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async getMathEquations() {
      // Zmiana aplikacji musi czyścić zaznaczone wzory. Bez tego wybór z poprzedniej
      // aplikacji zostawał w formularzu (v-select trzyma wartości spoza listy, więc
      // nie było ich widać) i wysyłał się razem z nowymi — plik wchodził podwójnie:
      // 21.09.2026 „T38 Uber Wloclawek" poszedł z wzorami Ubera I Bolta, 6 importów.
      this.mathEq = null;
      await this.toggleLoading(true);
      try {
        const mathEqs = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `import/math`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        const all = mathEqs.data.data;
        const slim = (list) => list.map((it) => ({ id: it.id, name: it.name }));
        this.mathEqs = slim(all.filter((it) => it.type === this.appType));
        // Sekcja API Bolta zawsze dostaje wyłącznie wzory BOLT, niezależnie od
        // tego, jaka aplikacja jest wybrana u góry.
        this.boltMathEqs = slim(all.filter((it) => it.type === 'BOLT'));
        if (this.apiEquation && !this.boltMathEqs.some((e) => e.id === this.apiEquation)) {
          this.apiEquation = null;
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async loadCities() {
      this.citiesLoading = true;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'bolt/cities',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.cities = res.data.data || [];
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie pobrano miast', 'error');
      }
      this.citiesLoading = false;
    },
    async autoCreateAllCities() {
      this.autoCreateLoading = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'bolt/cities/auto-create-all',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const created = res.data.data.created || [];
        if (created.length === 0) {
          Swal.fire('Info', 'Brak nowych oddziałów do utworzenia — wszystkie aktywne miasta już są zmapowane', 'info');
        } else {
          const names = created.map((c) => c.subCompanyName || `#${c.boltCompanyId}`).join(', ');
          Swal.fire('Sukces', `Utworzono ${created.length} oddział(-ów): ${names}`, 'success');
        }
        await this.loadCities();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się utworzyć oddziałów', 'error');
      }
      this.autoCreateLoading = false;
    },
    async createCityAsSubCompany(c) {
      this.cityActionLoading = c.boltCompanyId;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'bolt/cities/create',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { boltCompanyId: c.boltCompanyId, name: c.boltCompanyName },
        });
        Swal.fire('Sukces', `Utworzono oddział "${c.boltCompanyName}"`, 'success');
        await this.loadCities();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie utworzono oddziału', 'error');
      }
      this.cityActionLoading = null;
    },
    async toggleAutoImport() {
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'bolt/weekly/auto-import',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { enabled: this.autoImportEnabled },
        });
      } catch (e) {
        this.autoImportEnabled = !this.autoImportEnabled;
        Swal.fire('Błąd', 'Nie udało się zmienić ustawienia', 'error');
      }
    },
    async previewApi() {
      if (!this.apiFrom || !this.apiTo) {
        Swal.fire('Błąd', 'Brak zakresu dat', 'error');
        return;
      }
      this.apiLoading = true;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'bolt/weekly/preview',
          params: { from: this.apiFrom, to: this.apiTo },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.apiPreview = res.data.data;
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się pobrać', 'error');
      }
      this.apiLoading = false;
    },
    async runApi() {
      if (!this.apiFrom || !this.apiTo || !this.apiEquation) {
        Swal.fire('Błąd', 'Brak zakresu dat lub wzoru', 'error');
        return;
      }
      this.apiLoading = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'bolt/weekly/run',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { from: this.apiFrom, to: this.apiTo, equationId: this.apiEquation },
        });
        const d = res.data.data;
        Swal.fire('Sukces', `Zaimportowano ${d.rowCount} wierszy z ${d.cities.length} miast. importId: ${d.importId}`, 'success');
        this.apiPreview = null;
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zaimportować', 'error');
      }
      this.apiLoading = false;
    },
    async importFiles() {
      if (!this.date) {
        await Swal.fire("Błąd", "Brak Wybranej Daty!", "error");
        return false;
      }
      if (!this.file) {
        await Swal.fire("Błąd", "Brak pliku!", "error");
        return false;
      }
      await this.toggleLoading(true);
      try {
        const formData = new FormData();
        formData.append("file", this.file, this.file.name);
        formData.append("date", this.date);
        formData.append("equationId", this.mathEq);
        formData.append("fileName", this.fileName);
        const response = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "import",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.file = null;
        this.date = null;
        this.fileName = null;
        this.mathEq = null;
        const undone = response.data?.data?.undonePreliminaryCount || 0;
        const baseMsg = `Import zakończony.`;
        const undoneMsg = undone > 0
          ? `<br><br><b>Auto-cofnięto ${undone} wstępnych importów</b> z API z tego tygodnia. Bolt CSV jest źródłem prawdy.`
          : '';
        const results = response.data?.data?.results || [];
        let resultsMsg = '';
        let anySkipped = false;
        for (const r of results) {
          const skippedParts = [];
          if (r.unassignedRows > 0) {
            skippedParts.push(`<b>${r.unassignedRows}</b> z nieznanym ID`);
          }
          if (r.wrongCooperationTypeRows > 0) {
            skippedParts.push(`<b>${r.wrongCooperationTypeRows}</b> kierowców o innym typie współpracy`);
          }
          resultsMsg += `<br><br><b>${r.equationName || r.cooperationType || ''}</b>: przypisano ${r.assignedRows}/${r.totalRows} wierszy`;
          if (r.missingVariables && r.missingVariables.length) {
            anySkipped = true;
            resultsMsg += `<br><span style="color:#e65100">⚠ Wzór odwołuje się do nieistniejących kolumn (policzono jako 0): <b>${r.missingVariables.join(', ')}</b></span>`;
          }
          if (skippedParts.length) {
            anySkipped = true;
            resultsMsg += `<br>Pominięto: ${skippedParts.join(', ')}`;
          }
          if (r.errors > 0) {
            anySkipped = true;
            resultsMsg += `<br>Błędy przetwarzania: <b>${r.errors}</b>`;
            const details = r.errorDetails || [];
            for (const d of details) {
              const ex = (d.examples && d.examples.length)
                ? ` (np. ${d.examples.slice(0, 3).join(', ')})`
                : '';
              resultsMsg += `<br><span style="color:#b71c1c">• ${d.count}× ${d.message}${ex}</span>`;
            }
          }
        }
        if (anySkipped) {
          resultsMsg += `<br><br>Wiersze z nieznanym ID znajdziesz w Historii importów → "Pokaż pominięte".`;
        }
        Swal.fire({
          icon: anySkipped ? 'warning' : 'success',
          title: anySkipped ? 'Zaimportowano z pominięciami' : 'Sukces',
          html: baseMsg + undoneMsg + resultsMsg,
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      await this.toggleLoading(false);
    },
  },
};
</script>

<style scoped>
.uploader-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.content-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-color: #2A3142 !important;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.uploader-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.uploader-page >>> .v-text-field input,
.uploader-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.uploader-page >>> .v-label {
  color: #94A0B8 !important;
}
.uploader-page >>> .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.uploader-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.uploader-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.uploader-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.uploader-page >>> .v-alert {
  border-color: #2A3142 !important;
}
</style>
